import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  Divider,
  TextField,
  SwipeableDrawer,
  makeStyles,
  FormHelperText,
} from "@material-ui/core";
import { connect, useDispatch } from "react-redux";
import { BUTTON_COLOR } from "../../utils/constant";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { applied_offer, clear_applied_offer } from "../../store/actions/action";
import { addDiscount, removeDiscount } from "../../store/actions/cartActions";
import { routeOfferThroughEngine } from "../../utils/offer_router";

// Checks if an offer is currently active based on:
// - offer_header.effective_date / end_date (date range)
// - offer_scheduler.days[].day + time_frame (day/time slots)
// If no offer_scheduler is set, the offer is always visible.
const isOfferActiveByScheduler = (offer, storeInfo, storeInfoConfig) => {
  if (!offer) return false;

  // Date range check using offer_header.effective_date and end_date
  const now = new Date();
  const effectiveDate = offer?.offer_header?.effective_date;
  const endDate = offer?.offer_header?.end_date;
  if (effectiveDate && new Date(effectiveDate) > now) return false;
  if (endDate && new Date(endDate) < now) return false;

  // If no scheduler configured, offer is always active
  const scheduler = offer?.offer_scheduler;
  if (!scheduler || !Array.isArray(scheduler.days) || scheduler.days.length === 0) return true;

  // is_all_days: true means no day/time restriction
  if (scheduler.is_all_days === true) return true;

  // Get store timezone
  const timeZone =
    storeInfo?.store_timezone ||
    storeInfoConfig?.store_timezone ||
    storeInfoConfig?.general_config?.store_timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Current day as short name ("Mon", "Tue", ...) in store timezone
  const currentDay = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(now);

  // Current time as "HH:mm" in store timezone
  const timeParts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit", minute: "2-digit", hour12: false, hourCycle: "h23", timeZone,
  }).formatToParts(now);
  let hour = timeParts.find((p) => p.type === "hour")?.value || "00";
  if (hour === "24") hour = "00";
  const minute = (timeParts.find((p) => p.type === "minute")?.value || "00").padStart(2, "0");
  const currentTime = `${hour.padStart(2, "0")}:${minute}`;

  // Find the matching day entry
  const matchingDay = scheduler.days.find(
    (d) => (d?.day || "").toLowerCase() === currentDay.toLowerCase()
  );
  if (!matchingDay) return false; // today is not in the scheduler

  // No time_frame means whole day is active
  const timeFrames = matchingDay.time_frame;
  if (!timeFrames || !Array.isArray(timeFrames) || timeFrames.length === 0) return true;

  // Check if current time falls within any time_frame slot
  return timeFrames.some((tf) => {
    const start = tf?.start_time || "00:00";
    const end   = tf?.end_time   || "23:59";
    return currentTime >= start && currentTime <= end;
  });
};

function CartOffers(props) {
  const {
    open,
    setOpen,
    storeOffer,
    addedItems,
    cartProps,
    removeDiscount: removeDiscountAction,
    clear_applied_offer: clearAppliedOfferAction,
    applied_offer: appliedOfferAction,
    addDiscount: addDiscountAction,
  } = props;

  const dispatch = useDispatch();
  const storeInfoConfig = props.sessionReducer?.store_config;
  const storeId = props.sessionReducer?.store_details?.store_id;
  const orderType = cartProps?.orderType?.order?.order_type || "Dine In";
  const engineCtx = {
    orderType,
    storeId,
    storeConfig:   storeInfoConfig,
    paymentOption: cartProps?.selectedTender,
    tip:           cartProps?.tip || 0,
  };
  const [coupenCode, setCoupenCode] = useState("");
  const [appliedCoupen, setAppliedCoupen] = useState(false);
  const [invalidCoupen, setInvalidCoupen] = useState(false);
  const [filteredStoreOffer, setFilteredStoreOffer] = useState([]);
  const [userRemovedOffer, setUserRemovedOffer] = useState(false);

  // Use Redux appliedOffer as source of truth — localStorage can be stale after cart clear
  const offerDis = cartProps?.appliedOffer || [];

  let primaryColor = storeInfoConfig?.general_config?.primary_color;

  const useStyles = makeStyles({
    drawerPaper: {
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
    },
    searchInput: {
      background: "#eeeeee",
      borderRadius: "0.5rem",
      margin: "0px 5px",
      display: "flex",
      padding: "0rem 0.1rem",
    },
  });
  const classes = useStyles();

  const handleClose = async (e, type) => {
    if (type === "Remove") {
      setUserRemovedOffer(true);
      props.removeDiscount();
      props.clear_applied_offer();
      dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
      setOpen(false);
      return;
    }

    const termStyle = e?.offer_header?.term_style;

    let offerToApply = e;

    // Don't pre-clear engineDiscount / appliedOffer here — the empty state
    // moment triggers the auto-apply useEffect to pick a different offer,
    // which races with this manual apply. Let routeOfferThroughEngine's
    // dispatch atomically replace the current offer with the new one.
    // Also: don't reset userRemovedOffer. Once the user has taken any
    // explicit action (remove or explicit apply), auto-apply stays off for
    // the session — user has full manual control.
    props.applied_offer(offerToApply);
    props.addDiscount(offerToApply.offer_term, termStyle);
    const result = await routeOfferThroughEngine(offerToApply, addedItems, { ...engineCtx, entryPoint: "list" }, dispatch);
    // Engine is the sole authority — if it stamped zero discount the offer
    // doesn't apply, regardless of what local eligibility heuristics said.
    if (!result || !(result.total_discount > 0)) {
      console.warn("[CartOffers manual] engine stamped zero discount — clearing", offerToApply?.offer_id);
      props.removeDiscount();
      props.clear_applied_offer();
      dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
      setInvalidCoupen(true);
      return;
    }
    setOpen(false);
    setAppliedCoupen(!appliedCoupen);
  };

  const CartQty = addedItems
    .filter((item) => !item.is_void && !item.isVoid && !item.is_refund)
    .reduce((acc, currentItem) => acc + currentItem.quantity, 0);
  const CartTotal = cartProps.subTotal;
  const addedItemCategories = useMemo(() => addedItems.map((e) => e.category_id), [CartQty, CartTotal]);
  const addedItemId = useMemo(() => addedItems?.map((e) => e.item_variant[0]?.sku), [CartQty]);

let offer1 = useMemo(() => filteredStoreOffer?.map((dis) => {
  const terms = dis?.offer_term || [];
  const termStyle = dis?.offer_header?.term_style;
  const allTermProducts = dis?.offer_term_product || [];

  const checkItemConditions = (term) => {
    if (term?.is_include_items === false) return false;

    // Filter offer_term_product by this specific term's offer_term_id
    const termProducts = allTermProducts.filter(p => p.offer_term_id === term.offer_term_id);
    const disCategory = termProducts.map(e => e.category_id);
    const disItem = termProducts.map(e => e.item_id);
    const isAnyItemIdEmpty = disItem.some(id => id === "");

    if (!disCategory.some(categoryId => addedItemCategories.includes(categoryId)))
      return true;
    if (isAnyItemIdEmpty) return false;
    if (!disItem.some(itemId => addedItemId.includes(itemId))) return true;
    return false;
  };

  // Function to evaluate a single term
  const isTermDisabled = (term) => {
    const disCount = term?.count ?? "1";
    const disAmount = term?.amount ?? "0";
    const conditionType = term?.condition_type?.toLowerCase();

    const isCountValid = Number(disCount) <= CartQty;
    const isAmountValid = parseFloat(disAmount) <= CartTotal;

    // C2 coupon offers and Transaction offers skip item/category conditions for eligibility
    const skipItemCheck = term?.offering_type === "Transaction" || termStyle === "C2";

    if (conditionType === "both") {
      if (!isCountValid || !isAmountValid) return true;
      return skipItemCheck ? false : checkItemConditions(term);
    }

    if (conditionType === "count") {
      if (!isCountValid) return true;
      return skipItemCheck ? false : checkItemConditions(term);
    }

    if (conditionType === "amount") {
      if (!isAmountValid) return true;
      return skipItemCheck ? false : checkItemConditions(term);
    }

    return true; // if condition_type is unknown
  };

  if (termStyle === "C3") {
    const hasTransactionTerm = terms.some(t => t?.offering_type === "Transaction");
    if (hasTransactionTerm) return { ...dis, disable: true };
    const anyTermFails = terms.some(isTermDisabled);
    if (anyTermFails) return { ...dis, disable: true };
    // Also require enough total qty to form at least one BUY+GET pair
    const buyTerm = terms.find(t => parseFloat(t.discount_type_value) === 0);
    const getTerm = terms.find(t => parseFloat(t.discount_type_value) > 0);
    const buyCount = parseInt(buyTerm?.count ?? 1);
    const getCount = parseInt(getTerm?.count ?? 1);
    if (CartQty < buyCount + getCount) return { ...dis, disable: true };
    return { ...dis, disable: false };
  }
  // Default (non-C3) – only use the first term
  const disable = isTermDisabled(terms[0]);
  return { ...dis, disable };
}), [filteredStoreOffer, CartQty, CartTotal, addedItemCategories, addedItemId]);

  const sortedOffer1 = useMemo(() => {
    if (!offer1) return [];
    return [...offer1].sort((a, b) => {
    if (!a.disable && b.disable) {
      return -1;
    } else if (a.disable && !b.disable) {
      return 1;
    } else {
      return 0;
    }
  });
  }, [offer1]);

  useEffect(() => {
    const customer = props.customerReducer?.customer;
    const isLoggedIn = customer?.cust_mobile && customer?.guest !== "Guest";
    const membershipOfferIds = new Set();
    if (isLoggedIn) {
      const membershipData = props.customerReducer?.customerMembership || [];
      membershipData.forEach(membership => {
        membership.offer_details?.forEach(offer => {
          membershipOfferIds.add(offer.offer_id);
        });
      });
    }
    const storeInfo = props.sessionReducer?.store_details;
    const filtered = (storeOffer || []).filter(offer =>
      !membershipOfferIds.has(offer.offer_id) &&
      isOfferActiveByScheduler(offer, storeInfo, storeInfoConfig)
    );
    setFilteredStoreOffer(filtered);
  }, [storeOffer, props.customerReducer?.customerMembership, props.customerReducer?.customer, storeInfoConfig]);

  const isAutoApplyOffer = (o) => {
    const termStyle = o?.offer_header?.term_style;
    if (termStyle === "C3") return true;
    if (termStyle === "C1") {
      return o?.offer_term?.[0]?.offering_type === "Item";
    }
    return false;
  };

  const getEffectiveDiscountValue = (o) => {
    const termStyle = o?.offer_header?.term_style;
    const terms = o?.offer_term || [];
    const termProducts = o?.offer_term_product || [];

    // For C3, the discount term is the GET term (discount_type_value > 0)
    const term = termStyle === "C3"
      ? terms.find(t => parseFloat(t.discount_type_value) > 0)
      : terms[0];

    if (!term) return 0;

    const value = parseFloat(term.discount_type_value || 0);
    const disType = term.discount_type_code;
    const noPerTrans = String(term.no_pertrans ?? "1");

    // Determine qualifying items (respecting is_include_items + category/item filters)
    let qualifyingItems = addedItems;
    if (term.is_include_items) {
      const termProds = termProducts.filter(p => p.offer_term_id === term.offer_term_id);
      const isAnyItemIdEmpty = termProds.some(p => p.item_id === "");
      if (isAnyItemIdEmpty) {
        const catIds = termProds.map(p => p.category_id);
        qualifyingItems = addedItems.filter(i => catIds.includes(i.category_id));
      } else {
        const skus = termProds.map(p => String(p.item_id));
        qualifyingItems = addedItems.filter(i => skus.includes(String(i.item_variant[0]?.sku)));
      }
    }

    if (!qualifyingItems.length) return 0;

    const sortedPlus = qualifyingItems
      .map(item => parseFloat(item.item_variant[0]?.plu || 0))
      .sort((a, b) => a - b);
    const cheapestPlu = sortedPlus[0] ?? 0;

    if (noPerTrans === "0") {
      if (disType === "2") return (value / 100) * cheapestPlu;
      return Math.min(value, cheapestPlu);
    }

    if (disType === "2") return (value / 100) * CartTotal;
    return value;
  };

  useEffect(() => {
    if (!filteredStoreOffer?.length) return;
    if (userRemovedOffer) return;
    const currentApplied = offerDis;
    const eligibleOffers = offer1?.filter(o => isAutoApplyOffer(o) && !o.disable) || [];

    // Only one offer can be auto-applied at a time — pick the single best by
    // effective discount value and send just that to the engine. The engine
    // supports multi-offer stacking via applyItemLevelOffers, but product
    // decision is to keep auto-apply single so the user sees one predictable
    // offer instead of overlapping engine-selected combinations.
    const eligibleAutoApply = eligibleOffers.reduce((best, o) =>
      getEffectiveDiscountValue(o) > getEffectiveDiscountValue(best) ? o : best
    , eligibleOffers[0]);

    // If a manual/coupon offer is currently applied and it's not in the
    // auto-eligible set, leave it alone — the user (or the coupon flow)
    // chose it explicitly and the auto path must not stomp it.
    const currentIsManual = currentApplied?.offer_id
      && !eligibleOffers.some(o => o.offer_id === currentApplied.offer_id);

    // If the auto candidate is the same offer that's already applied and the
    // engine already stamped a discount, don't re-run — it's a no-op that
    // risks a spurious zero-discount clear on a bad re-run.
    const engineDiscount = cartProps?.engineDiscount;
    const sameOfferAlreadyStamped = eligibleAutoApply
      && currentApplied?.offer_id === eligibleAutoApply.offer_id
      && engineDiscount?.total_discount > 0;

    if (eligibleAutoApply && !currentIsManual && !sameOfferAlreadyStamped) {
      const termStyle = eligibleAutoApply.offer_header?.term_style;
      const isSwitchingOffer = currentApplied?.offer_id && currentApplied.offer_id !== eligibleAutoApply.offer_id;
      (async () => {
        if (isSwitchingOffer) {
          removeDiscountAction();
          clearAppliedOfferAction();
          dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        }
        appliedOfferAction(eligibleAutoApply);
        addDiscountAction(eligibleAutoApply.offer_term, termStyle);
        const result = await routeOfferThroughEngine(eligibleAutoApply, addedItems, { ...engineCtx, entryPoint: "auto" }, dispatch);
        if (!result || !(result.total_discount > 0)) {
          console.warn("[CartOffers auto] engine stamped zero discount — clearing");
          removeDiscountAction();
          clearAppliedOfferAction();
          dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        }
      })();
    } else if (!eligibleAutoApply && isAutoApplyOffer(currentApplied)) {
      removeDiscountAction();
      clearAppliedOfferAction();
      dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
    } else if (currentApplied?.offer_id) {
      const appliedInOffer1 = offer1?.find(o => o.offer_id === currentApplied.offer_id);
      const storeInfo = props.sessionReducer?.store_details;
      const isSchedulerActive = isOfferActiveByScheduler(currentApplied, storeInfo, storeInfoConfig);
      if (!appliedInOffer1 || appliedInOffer1?.disable || !isSchedulerActive) {
        removeDiscountAction();
        clearAppliedOfferAction();
        dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer1, userRemovedOffer]);

  // Re-evaluate applied offer whenever addedItems changes (e.g. quantity increased/decreased)
  useEffect(() => {
    const activeOffer = cartProps?.appliedOffer;
    if (!activeOffer || !addedItems?.length) return;
    const entryPoint = userRemovedOffer ? "coupon" : "auto";
    routeOfferThroughEngine(activeOffer, addedItems, { ...engineCtx, entryPoint }, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedItems]);

  const handleClick = async () => {
    if (coupenCode) {
      // Coupon path: find the offer by coupon_code in the active storeOffer
      // list matching scheduler — ignore the legacy `disable` heuristics.
      const code = coupenCode.trim().toLowerCase();
      const storeInfo = props.sessionReducer?.store_details;
      const matched = (storeOffer || []).find((o) =>
        isOfferActiveByScheduler(o, storeInfo, storeInfoConfig) && (
          (o?.offer_term || []).some(
            (t) => (t?.coupon_code || "").toLowerCase() === code
          )
          || (o?.promo_code || "").toLowerCase() === code
          || (o?.code || "").toLowerCase() === code
          || (o?.title || "").toLowerCase() === code
          || (o?.offer_name || "").toLowerCase() === code
        )
      );
      if (!matched) {
        console.log("[coupon] not matched by code or scheduler inactive", code, "storeOffer count:", (storeOffer || []).length);
        setInvalidCoupen(true);
        return;
      }

      // Don't pre-clear — see comment in handleClose. Router's DISPATCH
      // atomically replaces state.
      setUserRemovedOffer(true);
      props.applied_offer(matched);
      props.addDiscount(matched.offer_term, matched.offer_header?.term_style);
      const result = await routeOfferThroughEngine(matched, addedItems, { ...engineCtx, entryPoint: "coupon" }, dispatch);
      if (!result || !(result.total_discount > 0)) {
        console.warn("[CartOffers coupon] engine stamped zero discount — clearing", matched?.offer_id);
        props.removeDiscount();
        props.clear_applied_offer();
        dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        setInvalidCoupen(true);
        return;
      }
      setAppliedCoupen(true);
      setCoupenCode("");
      setOpen(false);
      setInvalidCoupen(false);
    }
  };

  const searchedOffers = useMemo(() => {
    const code = (coupenCode || "").trim().toLowerCase();
    const appliedOfferId =
      (Array.isArray(offerDis)
        ? offerDis[0]?.offer_id || offerDis[0]?._id
        : offerDis?.offer_id || offerDis?._id) || cartProps?.engineDiscount?.offer_id;

    if (!code) {
      // Default list: hide any offer that has a coupon/promo code,
      // UNLESS that discount is currently applied.
      return (sortedOffer1 || []).filter((offer) => {
        const isThisOfferApplied = Boolean(
          appliedOfferId &&
            (offer?.offer_id === appliedOfferId || offer?._id === appliedOfferId)
        );
        if (isThisOfferApplied) return true;

        const promoCodes = [
          offer?.promo_code,
          offer?.code,
          ...(offer?.offer_term || []).map((t) => t?.coupon_code),
        ]
          .filter(Boolean)
          .map((c) => String(c).trim().toLowerCase());
        return promoCodes.length === 0;
      });
    }

    return (sortedOffer1 || []).filter((offer) => {
      const isThisOfferApplied = Boolean(
        appliedOfferId &&
          (offer?.offer_id === appliedOfferId || offer?._id === appliedOfferId)
      );
      if (isThisOfferApplied) return true;

      const promoCodes = [
        offer?.promo_code,
        offer?.code,
        ...(offer?.offer_term || []).map((t) => t?.coupon_code),
        offer?.title,
        offer?.offer_name,
        offer?.offer_header?.deal_type,
      ]
        .filter(Boolean)
        .map((c) => String(c).trim().toLowerCase());

      return promoCodes.some((c) => c.includes(code));
    });
  }, [sortedOffer1, coupenCode, offerDis, cartProps?.engineDiscount?.offer_id]);

  return (
    <div>

      <SwipeableDrawer
        open={open}
        anchor={"bottom"}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        disableSwipeToOpen={true}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {filteredStoreOffer?.length ? (
          <Typography style={{ padding: "2rem 2rem 0rem 1rem" }}>
            <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: primaryColor }}>
              Enter Promo Code
            </span>
            <br />
            {/* to get discount */}
          </Typography>
        ) : (
          <Typography style={{ padding: "2rem" }}>
            You don't have any offers right now.
          </Typography>
        )}
        <ClearIcon
          style={{
            marginRight: "0.5rem",
            position: "absolute",
            top: "2rem",
            right: "15px",color: primaryColor
          }}
          onClick={() => setOpen(false)}
        />

        {filteredStoreOffer?.length ? (
          <Box sx={{ padding: "1rem" }} role="presentation">
            <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
              <div style={{
                flex: 1,
                border: `1.5px solid ${primaryColor || BUTTON_COLOR}`,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                padding: "0 10px",
                background: "#fff"
              }}>
                <input
                  type="text"
                  placeholder="Enter Coupon Code"
                  value={coupenCode}
                  onChange={(e) => setCoupenCode(e.target.value.toUpperCase())}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    padding: "12px 0",
                    fontSize: "1rem",
                    background: "transparent",
                    color: "#333"
                  }}
                />
                {coupenCode && (
                  <ClearIcon
                    style={{ color: "#999", cursor: "pointer", fontSize: "1.2rem" }}
                    onClick={() => setCoupenCode("") + setInvalidCoupen(false)}
                  />
                )}
              </div>
              <button
                onClick={() => handleClick()}
                style={{
                  background: primaryColor || BUTTON_COLOR,
                  color: "#fff",
                  border: "none",
                  padding: "0 24px",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                Apply
              </button>
            </div>
            <FormHelperText style={{ marginLeft: "0.4rem" }}>
              {invalidCoupen && "Invalid Coupon Code"}
            </FormHelperText>
            <Divider style={{ marginTop: "1.5rem" }}></Divider>

            <Grid style={{ maxHeight: "40vh", overflow: "scroll" }}>
              {searchedOffers?.map((e) => {
                const appliedOfferId =
                  (Array.isArray(offerDis)
                    ? offerDis[0]?.offer_id || offerDis[0]?._id
                    : offerDis?.offer_id || offerDis?._id) || cartProps?.engineDiscount?.offer_id;
                const isThisOfferApplied = Boolean(
                  appliedOfferId &&
                    (e?.offer_id === appliedOfferId || e?._id === appliedOfferId)
                );
                const isItemDisabled = e?.disable && !isThisOfferApplied;

                return (
                <Grid
                  key={e.offer_id}
                  style={{
                    display: "flex",
                    margin: "10px 0",
                    border: isThisOfferApplied ? `1.5px solid ${primaryColor || BUTTON_COLOR}` : "1px solid #e0e0e0",
                    borderLeft:"none",
                    borderRadius: "6px",
                    overflow: "hidden",
                    background: "#fff"
                  }}
                >
                  {/* Left Ticket Stub */}
                  <div style={{
                    width: "24px",
                    backgroundColor: isItemDisabled ? "#bdbdbd" : (primaryColor || BUTTON_COLOR),
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "flex-start"
                  }}>
                    <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }}></div>
                    <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }}></div>
                    <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }}></div>
                  </div>

                  {/* Right Content */}
                  <div style={{
                    flex: 1,
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px"
                  }}>
                    {/* Top Row: Title & Button */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "1rem", fontWeight: "600", color: isItemDisabled ? "#979797" : "#333", paddingRight: "10px" }}>
                        {e?.offer_header?.deal_type}
                      </span>
                      <button
                        disabled={isItemDisabled}
                        onClick={() =>
                            handleClose(
                              e,
                              isThisOfferApplied ? "Remove" : "Apply"
                            )
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          color: isItemDisabled ? "#bdbdbd" : (primaryColor || BUTTON_COLOR),
                          fontWeight: "bold",
                          fontSize: "0.95rem",
                          cursor: isItemDisabled ? "not-allowed" : "pointer",
                          textTransform: "uppercase",
                          padding: "0",
                          lineHeight: "1.2rem",
                          flexShrink: 0 // prevents button from wrapping/squishing if title is long
                        }}
                      >
                        {isThisOfferApplied ? "REMOVE" : "APPLY"}
                      </button>
                    </div>
                    {/* Bottom Row: Description */}
                    <span style={{ fontSize: "0.85rem", color: isItemDisabled ? "#bdbdbd" : "#888", paddingRight: "40px" }}>
                      {e?.offer_header?.terms_conditions}
                    </span>
                  </div>
                </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : (
          ""
        )}
      </SwipeableDrawer>
    </div>
  );
}
const mapStateToProps = (state) => {
  return {
    reducer: state.customerReducer,
    customerReducer: state.customerReducer,
    sessionReducer: state.sessionReducer,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    applied_offer: (offer) => {
      dispatch(applied_offer(offer));
    },
    addDiscount: (id, termStyle) => {
      dispatch(addDiscount(id, termStyle));
    },
    removeDiscount: (id) => {
      dispatch(removeDiscount(id));
    },
    clear_applied_offer: () => {
      dispatch(clear_applied_offer());
    },
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(CartOffers);
